/**
 * The landscape behind the content follows the scroll position: once night has half fallen, the content
 * switches to its night colors (.scene.is-night). Browsers without scroll-driven animations also get the
 * scroll progress that sets the sun and lets night fall from here (see --scene-progress).
 */
const scene = document.querySelector('.scene');

if (scene) {
  const layers = scene.querySelectorAll('.scene__night, .scene__stars, .scene__sun, .scene__clouds');
  const animated = CSS.supports('animation-timeline: scroll()');

  const update = () => {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const progress = maxScroll > 0 ? Math.min(window.scrollY / maxScroll, 1) : 0;
    scene.classList.toggle('is-night', progress >= 0.5);
    if (!animated) layers.forEach((layer) => layer.style.setProperty('--scene-progress', progress));
  };

  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);

  const stars = scene.querySelector('.scene__stars');
  if (stars && window.matchMedia('(pointer: coarse)').matches) followCompass(stars);
}

/**
 * On phones the stars follow the compass and the tilt: turning around slides the sky sideways, tilting the
 * phone up or down slides it vertically. The star map spans the whole sky at 4px per degree (1440px for 360°
 * around, 720px for 180° from pole to pole) and repeats, so the sky wraps without a seam in both directions.
 * iOS only shares the compass after a tap, so it asks on the first tap once the stars are out.
 */
function followCompass(stars) {
  const pixelsPerDegree = 4;
  const root = document.documentElement;
  let target = null;
  let current = null;
  let frame = 0;

  // The turn from one angle to another the short way, so 359° to 1° turns 2° instead of spinning back around.
  const turn = (from, to) => ((((to - from) % 360) + 540) % 360) - 180;

  const draw = () => {
    current.x += (target.x - current.x) * 0.15;
    current.y += (target.y - current.y) * 0.15;
    // Set on the page, so the stars in the footer move along.
    root.style.setProperty('--stars-offset-x', `${(-current.x * pixelsPerDegree).toFixed(1)}px`);
    root.style.setProperty('--stars-offset-y', `${(current.y * pixelsPerDegree).toFixed(1)}px`);
    const moving = Math.abs(target.x - current.x) > 0.05 || Math.abs(target.y - current.y) > 0.05;
    frame = moving ? requestAnimationFrame(draw) : 0;
  };

  // The heading is where the back of the phone points, clockwise from north. The tilt (beta) is 90° when
  // the phone stands upright facing the horizon; looking up shows the sky above, so the stars move down.
  // Sideways the tilt comes from another axis, so the stars then only follow the compass.
  const setDirection = (heading, beta) => {
    const angle = screen.orientation?.angle ?? 0;
    heading += angle;
    const pitch = angle === 0 && beta != null ? beta - 90 : null;
    if (target === null) {
      target = { x: heading, y: pitch ?? 0 };
      current = { ...target };
    } else {
      target.x += turn(target.x, heading);
      if (pitch !== null) target.y += turn(target.y, pitch);
    }
    if (!frame) frame = requestAnimationFrame(draw);
  };

  if (typeof window.DeviceOrientationEvent?.requestPermission === 'function') {
    const ask = () => {
      if (Number(getComputedStyle(stars).opacity) === 0) return;
      document.removeEventListener('click', ask);
      DeviceOrientationEvent.requestPermission()
        .then((state) => {
          if (state !== 'granted') return;
          window.addEventListener('deviceorientation', (event) => {
            if (event.webkitCompassHeading != null) setDirection(event.webkitCompassHeading, event.beta);
          });
        })
        .catch(() => {});
    };
    document.addEventListener('click', ask);
  } else {
    window.addEventListener('deviceorientationabsolute', (event) => {
      if (event.alpha != null) setDirection(360 - event.alpha, event.beta);
    });
  }
}
